import { useMemo, useState } from "react";

import { cn } from "../lib/utils";

function buildMailto(values) {
  const subject = `iValue RegTech enquiry — ${values.company || values.name || ""}`.trim();
  const bodyLines = [
    `Name: ${values.name}`,
    `Email: ${values.email}`,
    values.phone ? `Phone: ${values.phone}` : "",
    values.company ? `Company: ${values.company}` : "",
    values.interest ? `Interest: ${values.interest}` : "",
    values.framework ? `Framework: ${values.framework}` : "",
    "",
    "Message:",
    values.message || "(no message)",
  ].filter(Boolean);

  const params = new URLSearchParams({
    subject,
    body: bodyLines.join("\n"),
  });

  return `mailto:info@ivalue.in?${params.toString()}`;
}

export function ContactForm({
  className,
  title = "Contact iValue",
  subtitle = "Share your compliance goals and we will respond with a tailored assessment / roadmap.",
  ctaLabel = "Send message",
}) {
  const [values, setValues] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    interest: "Assessment",
    framework: "DPDPA",
    message: "",
  });

  const mailto = useMemo(() => buildMailto(values), [values]);

  return (
    <form
      className={cn("grid gap-3", className)}
      onSubmit={(e) => {
        e.preventDefault();
        window.location.href = mailto;
      }}
    >
      <div className="grid gap-3 md:grid-cols-2">
        <input
          required
          value={values.name}
          onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
          className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-3 text-sm text-text-primary placeholder:text-text-tertiary"
          placeholder="Name*"
        />
        <input
          required
          type="email"
          value={values.email}
          onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
          className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-3 text-sm text-text-primary placeholder:text-text-tertiary"
          placeholder="Work email*"
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <input
          value={values.company}
          onChange={(e) => setValues((v) => ({ ...v, company: e.target.value }))}
          className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-3 text-sm text-text-primary placeholder:text-text-tertiary"
          placeholder="Company"
        />
        <input
          value={values.phone}
          onChange={(e) => setValues((v) => ({ ...v, phone: e.target.value }))}
          className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-3 text-sm text-text-primary placeholder:text-text-tertiary"
          placeholder="Phone"
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <select
          value={values.interest}
          onChange={(e) => setValues((v) => ({ ...v, interest: e.target.value }))}
          className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-3 text-sm font-semibold text-text-primary"
        >
          <option value="Assessment">Assessment</option>
          <option value="Workshop">Expert workshop</option>
          <option value="Implementation">Implementation</option>
          <option value="Managed Service">Managed continuous compliance</option>
          <option value="Tech Stack">Privacy / RegTech stack selection</option>
        </select>

        <select
          value={values.framework}
          onChange={(e) => setValues((v) => ({ ...v, framework: e.target.value }))}
          className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-3 text-sm font-semibold text-text-primary"
        >
          <option value="DPDPA">DPDPA</option>
          <option value="RBI">RBI</option>
          <option value="CERT-In">CERT-In</option>
          <option value="SEBI CSCRF">SEBI CSCRF</option>
          <option value="IRDAI">IRDAI</option>
          <option value="ISO 27001">ISO 27001</option>
          <option value="PCI-DSS">PCI-DSS</option>
          <option value="NIST CSF">NIST CSF</option>
          <option value="DORA">DORA</option>
          <option value="OT/ICS">OT/ICS</option>
          <option value="Multiple">Multiple</option>
        </select>
      </div>

      <textarea
        value={values.message}
        onChange={(e) => setValues((v) => ({ ...v, message: e.target.value }))}
        rows={5}
        className="focus-ring resize-y rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary"
        placeholder="Tell us what you need: target regulations, timelines, current tools, evidence gaps…"
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-sm font-semibold text-text-primary">{title}</div>
          <div className="mt-1 text-xs text-text-tertiary">{subtitle}</div>
        </div>

        <button
          type="submit"
          className="focus-ring sheen inline-flex h-11 items-center justify-center rounded-xl bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] px-5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
        >
          {ctaLabel}
        </button>
      </div>

      <div className="text-xs text-text-tertiary">
        This form opens your email client and pre-fills the message. CRM / form backend integration can be added next.
      </div>
    </form>
  );
}
